import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import * as Device from 'expo-device';

import CONFIG from '../config/Config';
import { AuthContext } from './AuthContext';

const EXPO_PUSH_TOKEN_KEY = 'expoPushToken';
const PERMISSION_STATUS_KEY = 'notificationPermissionStatus';
const PUSH_ENABLED_KEY = 'pushNotificationsEnabled';
const SOUND_ENABLED_KEY = 'soundNotificationsEnabled';
const PUSH_CHANNEL_ID = 'siparisler_v4';
const PUSH_SOUND_FILE = 'new_order.mp3';

export const NotificationsContext = createContext({
  expoPushToken: null,
  permissionStatus: 'undetermined',
  hasPermission: false,
  isRegistering: false,
  syncState: 'idle',
  registrationError: null,
  lastNotification: null,
  notificationResponse: null,
  pushEnabled: true,
  soundEnabled: true,
  requestPermissions: async () => null,
  refreshPermissions: async () => 'undetermined',
  retrySync: async () => {},
  setPushEnabled: async () => {},
  setSoundEnabled: async () => {},
});

function parseUser(userToken) {
  if (!userToken) {
    return null;
  }

  try {
    return JSON.parse(userToken);
  } catch (error) {
    console.log('[PUSH] userToken parse hatasi:', error?.message);
    return null;
  }
}

function getProjectId() {
  return Constants?.expoConfig?.extra?.eas?.projectId || Constants?.easConfig?.projectId || null;
}

function isExpoGoClient() {
  return Constants?.executionEnvironment === 'storeClient' || Constants?.appOwnership === 'expo';
}

let notificationsModule = null;

function getNotificationsModule() {
  if (isExpoGoClient()) {
    return null;
  }

  if (notificationsModule) {
    return notificationsModule;
  }

  try {
    // expo-notifications Expo Go'da uzaktan push tarafini desteklemez.
    notificationsModule = require('expo-notifications');
    return notificationsModule;
  } catch (error) {
    console.log('[PUSH] expo-notifications yuklenemedi:', error?.message);
    return null;
  }
}

function removeNotificationSub(Notifications, subscription) {
  if (!subscription) {
    return;
  }

  if (typeof subscription.remove === 'function') {
    subscription.remove();
    return;
  }

  if (Notifications && typeof Notifications.removeNotificationSubscription === 'function') {
    Notifications.removeNotificationSubscription(subscription);
  }
}

function buildUserId(userInfo) {
  if (!userInfo) {
    return null;
  }

  return userInfo.id || userInfo.ID || userInfo.UserID || userInfo.userId || null;
}

function buildEndpointCandidates(path) {
  if (!path) {
    return [];
  }

  if (path.startsWith('http')) {
    return [path];
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const base = CONFIG.API_BASE_URL || '';
  const candidates = [`${base}${normalizedPath}`];

  if (normalizedPath.startsWith('/push/')) {
    candidates.push(`${base}/KASAP${normalizedPath}`);
  }

  return [...new Set(candidates)];
}

async function postFormWithFallback(endpointCandidates, formData, label) {
  let lastError = null;

  for (const endpoint of endpointCandidates) {
    try {
      console.log(`[PUSH] ${label} endpoint:`, endpoint);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-App-Env': CONFIG.ENV,
        },
        body: formData,
      });

      if (!response.ok) {
        let body = '';
        try { body = await response.text(); } catch {}
        throw new Error(`HTTP ${response.status}: ${body.slice(0, 200)}`);
      }

      try {
        const data = await response.json();
        if (data?.success === false) {
          throw new Error(data?.message || 'Islem reddedildi');
        }
        return { success: true, data };
      } catch {
        return { success: true };
      }
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('Endpointlere ulasilamadi');
}

async function syncPushTokenWithBackend(expoPushToken, userInfo) {
  if (!CONFIG.PUSH_TOKEN_REGISTER_PATH) {
    return { skipped: true };
  }

  const endpointCandidates = buildEndpointCandidates(CONFIG.PUSH_TOKEN_REGISTER_PATH);

  const formData = new FormData();
  formData.append('expoPushToken', expoPushToken);
  formData.append('platform', Platform.OS);
  formData.append('appVersion', Constants?.expoConfig?.version || '1.0.0');
  formData.append('deviceName', Device.deviceName || '');
  formData.append('deviceModel', Device.modelName || '');
  formData.append('userId', String(buildUserId(userInfo) || ''));
  formData.append('userName', userInfo?.Name || userInfo?.username || '');

  return postFormWithFallback(endpointCandidates, formData, 'sync');
}

async function syncPushPreferencesWithBackend(expoPushToken, userInfo, pushEnabled, soundEnabled) {
  if (!CONFIG.PUSH_PREFERENCES_PATH) {
    return { skipped: true };
  }

  const endpointCandidates = buildEndpointCandidates(CONFIG.PUSH_PREFERENCES_PATH);

  const formData = new FormData();
  formData.append('expoPushToken', expoPushToken);
  formData.append('userId', String(buildUserId(userInfo) || ''));
  formData.append('pushEnabled', String(pushEnabled));
  formData.append('soundEnabled', String(soundEnabled));

  return postFormWithFallback(endpointCandidates, formData, 'preferences');
}

export function NotificationsProvider({ children }) {
  const { userToken } = useContext(AuthContext);

  const [expoPushToken, setExpoPushToken] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('undetermined');
  const [lastNotification, setLastNotification] = useState(null);
  const [notificationResponse, setNotificationResponse] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [syncState, setSyncState] = useState('idle');
  const [syncError, setSyncError] = useState(null);
  const [registrationError, setRegistrationError] = useState(null);
  const [pushEnabled, setPushEnabledState] = useState(true);
  const [soundEnabled, setSoundEnabledState] = useState(true);

  const pushEnabledRef = useRef(true);
  const soundEnabledRef = useRef(true);
  const notificationListener = useRef(null);
  const responseListener = useRef(null);
  const syncedTokenRef = useRef(null);
  const syncedPreferencesRef = useRef(null);

  // Daha önce senkron edilen token'ı hatırla
  useEffect(() => {
    AsyncStorage.getItem('syncedPushToken').then(v => {
      if (v) syncedTokenRef.current = v;
    }).catch(() => {});
  }, []);

  async function updatePushEnabled(value) {
    setPushEnabledState(true);
    pushEnabledRef.current = true;
    await AsyncStorage.setItem(PUSH_ENABLED_KEY, 'true');

    if (!value) {
      return;
    }

    const userInfo = parseUser(userToken);
    if (!userInfo || permissionStatus !== 'granted') {
      return;
    }

    try {
      setSyncState('syncing');
      setSyncError(null);

      if (value) {
        const tokenToSync = expoPushToken || await registerForPushNotifications();
        if (!tokenToSync) {
          throw new Error('Expo push token alinamadi');
        }
        syncedTokenRef.current = null;
        await syncPushTokenWithBackend(tokenToSync, userInfo);
        await syncPushPreferencesWithBackend(tokenToSync, userInfo, true, soundEnabledRef.current);
        syncedTokenRef.current = tokenToSync;
        syncedPreferencesRef.current = `${tokenToSync}|true|${String(soundEnabledRef.current)}`;
        setSyncState('synced');
      } else {
        const tokenToSync = expoPushToken || await registerForPushNotifications();
        if (!tokenToSync) {
          throw new Error('Expo push token alinamadi');
        }
        await syncPushPreferencesWithBackend(tokenToSync, userInfo, false, soundEnabledRef.current);
        syncedPreferencesRef.current = `${tokenToSync}|false|${String(soundEnabledRef.current)}`;
        setSyncState('synced');
      }
    } catch (error) {
      const errMsg = error?.message || 'Bilinmeyen hata';
      setSyncState('error');
      setSyncError(errMsg);
      console.log('[PUSH] push toggle senkron hatasi:', errMsg);
    }
  }

  async function updateSoundEnabled(value) {
    setSoundEnabledState(true);
    soundEnabledRef.current = true;
    await AsyncStorage.setItem(SOUND_ENABLED_KEY, 'true');

    if (!value) {
      return;
    }

    const userInfo = parseUser(userToken);
    if (!userInfo || !expoPushToken || permissionStatus !== 'granted') {
      return;
    }

    try {
      await syncPushPreferencesWithBackend(expoPushToken, userInfo, pushEnabledRef.current, value);
      syncedPreferencesRef.current = `${expoPushToken}|${String(pushEnabledRef.current)}|${String(value)}`;
    } catch (error) {
      const errMsg = error?.message || 'Bilinmeyen hata';
      setSyncState('error');
      setSyncError(errMsg);
      console.log('[PUSH] ses tercihi senkron hatasi:', errMsg);
    }
  }

  useEffect(() => {
    async function restoreCachedState() {
      try {
        const [cachedToken, cachedPermissionStatus, cachedPushEnabled, cachedSoundEnabled] = await Promise.all([
          AsyncStorage.getItem(EXPO_PUSH_TOKEN_KEY),
          AsyncStorage.getItem(PERMISSION_STATUS_KEY),
          AsyncStorage.getItem(PUSH_ENABLED_KEY),
          AsyncStorage.getItem(SOUND_ENABLED_KEY),
        ]);

        if (cachedToken) {
          setExpoPushToken(cachedToken);
        }

        if (cachedPermissionStatus) {
          setPermissionStatus(cachedPermissionStatus);
        }

        setPushEnabledState(true);
        pushEnabledRef.current = true;
        setSoundEnabledState(true);
        soundEnabledRef.current = true;

        if (cachedPushEnabled !== 'true') {
          await AsyncStorage.setItem(PUSH_ENABLED_KEY, 'true');
        }

        if (cachedSoundEnabled !== 'true') {
          await AsyncStorage.setItem(SOUND_ENABLED_KEY, 'true');
        }
      } catch (error) {
        console.log('[PUSH] cache okunamadi:', error?.message);
      }
    }

    restoreCachedState();
  }, []);

  useEffect(() => {
    const Notifications = getNotificationsModule();
    if (!Notifications) {
      return undefined;
    }

    try {
      Notifications.setNotificationHandler({
        handleNotification: async () => ({
          shouldShowBanner: true,
          shouldShowList: true,
          shouldPlaySound: true,
          shouldSetBadge: false,
        }),
      });
    } catch (error) {
      console.log('[PUSH] notification handler ayarlanamadi:', error?.message);
    }

    return undefined;
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'android') {
      return undefined;
    }

    const Notifications = getNotificationsModule();
    if (!Notifications) {
      return undefined;
    }

    const channelConfig = {
      name: 'Siparis Bildirimleri',
      importance: Notifications.AndroidImportance.MAX,
      vibrationPattern: [0, 250, 200, 250],
      lightColor: '#FF9800',
      sound: PUSH_SOUND_FILE,
      enableVibrate: true,
      lockscreenVisibility: Notifications.AndroidNotificationVisibility.PUBLIC,
    };

    Notifications.deleteNotificationChannelAsync(PUSH_CHANNEL_ID)
      .catch(() => {})
      .then(() =>
        Notifications.setNotificationChannelAsync(PUSH_CHANNEL_ID, channelConfig)
      )
      .catch((error) => {
        console.log('[PUSH] kanal olusturulamadi:', error?.message);
      });

    return undefined;
  }, []);

  async function refreshPermissions() {
    const Notifications = getNotificationsModule();
    if (!Notifications) {
      setPermissionStatus('expo-go-unsupported');
      await AsyncStorage.setItem(PERMISSION_STATUS_KEY, 'expo-go-unsupported');
      return 'expo-go-unsupported';
    }

    const permissions = await Notifications.getPermissionsAsync();
    const status = permissions.status || 'undetermined';

    setPermissionStatus(status);
    await AsyncStorage.setItem(PERMISSION_STATUS_KEY, status);

    return status;
  }

  async function registerForPushNotifications() {
    if (isRegistering) {
      return expoPushToken;
    }

    // Expo Go SDK 53+ icinde uzak push token uretimi desteklenmiyor.
    if (isExpoGoClient()) {
      setPermissionStatus('expo-go-unsupported');
      await AsyncStorage.setItem(PERMISSION_STATUS_KEY, 'expo-go-unsupported');
      return null;
    }

    if (!Device.isDevice) {
      setPermissionStatus('device-required');
      await AsyncStorage.setItem(PERMISSION_STATUS_KEY, 'device-required');
      return null;
    }

    setIsRegistering(true);
    setRegistrationError(null);

    try {
      let status = await refreshPermissions();
      const Notifications = getNotificationsModule();

      if (!Notifications) {
        setPermissionStatus('expo-go-unsupported');
        await AsyncStorage.setItem(PERMISSION_STATUS_KEY, 'expo-go-unsupported');
        return null;
      }

      if (status !== 'granted') {
        const permissionRequest = await Notifications.requestPermissionsAsync();
        status = permissionRequest.status || 'undetermined';
        setPermissionStatus(status);
        await AsyncStorage.setItem(PERMISSION_STATUS_KEY, status);
      }

      if (status !== 'granted') {
        return null;
      }

      const projectId = getProjectId();
      if (!projectId) {
        throw new Error('Expo projectId bulunamadi');
      }

      const tokenResponse = await Notifications.getExpoPushTokenAsync({ projectId });
      const nextToken = tokenResponse.data;

      setExpoPushToken(nextToken);
      await AsyncStorage.setItem(EXPO_PUSH_TOKEN_KEY, nextToken);

      return nextToken;
    } catch (error) {
      const errMsg = error?.message || 'Bilinmeyen hata';
      setRegistrationError(errMsg);
      console.log('[PUSH] kayit hatasi:', errMsg);
      return null;
    } finally {
      setIsRegistering(false);
    }
  }

  useEffect(() => {
    const Notifications = getNotificationsModule();
    if (!Notifications) {
      setPermissionStatus('expo-go-unsupported');
      AsyncStorage.setItem(PERMISSION_STATUS_KEY, 'expo-go-unsupported').catch(() => {});
      return undefined;
    }

    Notifications.getLastNotificationResponseAsync()
      .then((response) => {
        if (response) {
          setNotificationResponse(response);
        }
      })
      .catch((error) => {
        console.log('[PUSH] son notification response okunamadi:', error?.message);
      });

    if (parseUser(userToken)) {
      registerForPushNotifications();
    }

    notificationListener.current = Notifications.addNotificationReceivedListener((notification) => {
      setLastNotification(notification);
    });

    responseListener.current = Notifications.addNotificationResponseReceivedListener((response) => {
      setLastNotification(response.notification);
      setNotificationResponse(response);
    });

    return () => {
      removeNotificationSub(Notifications, notificationListener.current);
      removeNotificationSub(Notifications, responseListener.current);
      notificationListener.current = null;
      responseListener.current = null;
    };
  }, [userToken]);

  useEffect(() => {
    const userInfo = parseUser(userToken);

    if (!userInfo || permissionStatus !== 'granted') {
      if (!userToken) {
        syncedTokenRef.current = null;
        syncedPreferencesRef.current = null;
        setSyncState('idle');
      }
      return;
    }

    if (!expoPushToken) {
      setSyncState('syncing');
      registerForPushNotifications().catch((error) => {
        const errMsg = error?.message || 'Bilinmeyen hata';
        setSyncState('error');
        setSyncError(errMsg);
      });
      return;
    }

    if (syncedTokenRef.current === expoPushToken) {
      return;
    }

    let isMounted = true;

    async function syncToken() {
      try {
        setSyncState('syncing');
        setSyncError(null);
        const result = await syncPushTokenWithBackend(expoPushToken, userInfo);

        if (!isMounted) {
          return;
        }

        if (result?.skipped) {
          setSyncState('pending-backend');
          return;
        }

        syncedTokenRef.current = expoPushToken;
        await AsyncStorage.setItem('syncedPushToken', expoPushToken);
        setSyncState('synced');
      } catch (error) {
        if (!isMounted) {
          return;
        }

        const errMsg = error?.message || 'Bilinmeyen hata';
        setSyncState('error');
        setSyncError(errMsg);
        console.log('[PUSH] backend senkron hatasi:', errMsg);
      }
    }

    syncToken();

    return () => {
      isMounted = false;
    };
  }, [expoPushToken, permissionStatus, userToken]);

  // Preferences her zaman true (stabil mod) - sadece token değiştiğinde senkronla
  useEffect(() => {
    const userInfo = parseUser(userToken);
    if (!userInfo || !expoPushToken || permissionStatus !== 'granted') {
      return;
    }

    // Token sync zaten preferences'ı da kapsar, ayrıca göndermeye gerek yok
  }, [expoPushToken, permissionStatus, pushEnabled, soundEnabled, userToken]);

  return (
    <NotificationsContext.Provider
      value={{
        expoPushToken,
        permissionStatus,
        hasPermission: permissionStatus === 'granted',
        isRegistering,
        syncState,
        syncError,
        registrationError,
        lastNotification,
        notificationResponse,
        pushEnabled,
        soundEnabled,
        setPushEnabled: updatePushEnabled,
        setSoundEnabled: updateSoundEnabled,
        requestPermissions: registerForPushNotifications,
        refreshPermissions,
        retrySync: async () => {
          const userInfo = parseUser(userToken);
          if (!expoPushToken || !userInfo || permissionStatus !== 'granted') return;
          syncedTokenRef.current = null;
          syncedPreferencesRef.current = null;
          try {
            setSyncState('syncing');
            setSyncError(null);
            const result = await syncPushTokenWithBackend(expoPushToken, userInfo);
            if (!result?.skipped) {
              syncedTokenRef.current = expoPushToken;
              await syncPushPreferencesWithBackend(expoPushToken, userInfo, pushEnabled, soundEnabled);
              syncedPreferencesRef.current = `${expoPushToken}|${String(pushEnabled)}|${String(soundEnabled)}`;
              setSyncState('synced');
            } else {
              setSyncState('pending-backend');
            }
          } catch (error) {
            const errMsg = error?.message || 'Bilinmeyen hata';
            setSyncState('error');
            setSyncError(errMsg);
            console.log('[PUSH] retry senkron hatasi:', errMsg);
          }
        },
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}